"use client";


import type {
  RootLayoutCampaignFragment,
  RootLayoutQuery,
  RootLayoutSiteinfoFragment,
  RootLayoutWishlistFragment,
} from "@server/gql/graphql";
import type { PropsWithChildren } from "react";
import { createContext, useContext, useMemo } from "react";

import type { MarketConfiguration } from "@/config/markets/const";
import { minimumOrderValueSchema } from "@/schemas/minimumOrderValue.schema";
import type { PageMapEntry } from "@server/requests/contentful/getPageByType";

type SiteInfoContextProps = {
  wishlist?: RootLayoutWishlistFragment | null;
  campaigns?: (RootLayoutCampaignFragment | null)[];
  campaignPriorityCollection?: RootLayoutSiteinfoFragment["campaignPriorityCollection"];
  sideMenuMyPages?: RootLayoutSiteinfoFragment["sideMenuMyPages"];
  myPagesRegisterPage?: RootLayoutSiteinfoFragment["myPagesRegisterPage"];
  myPagesStartPage?: RootLayoutSiteinfoFragment["myPagesStartPage"];
  sideMenuServicePages?: RootLayoutSiteinfoFragment["sideMenuServicePages"];
  minimumOrderValue?: number;
  // Always a concrete boolean: a missing/null Contentful field is treated as
  // `false` (disclaimer off) — see the provider's `?? false` below.
  showCheckoutMadeToOrderDisclaimer: boolean;
  pages?: Map<string, PageMapEntry>;
};

export const SiteInfoContext = createContext<SiteInfoContextProps | undefined>(
  undefined,
);

type SiteInfoProviderProps = PropsWithChildren<{
  siteinfoCollection?: RootLayoutQuery["siteinfoCollection"];
  campaigns?: RootLayoutQuery["campaigns"];
  wishlist?: RootLayoutQuery["wishlist"];
  marketConfig: MarketConfiguration;
  pages?: Map<string, PageMapEntry>;
}>;

export const SiteInfoProvider = ({
  siteinfoCollection,
  wishlist,
  campaigns,
  marketConfig,
  children,
  pages,
}: SiteInfoProviderProps) => {
  const { data: minimumOrderValue } = minimumOrderValueSchema.safeParse(
    siteinfoCollection?.items[0]?.minimumOrderValue,
  );

  const value = useMemo(() => {
    return {
      wishlist: wishlist?.items[0],
      campaigns: campaigns?.items,
      campaignPriorityCollection:
        siteinfoCollection?.items[0]?.campaignPriorityCollection,
      sideMenuMyPages: siteinfoCollection?.items[0]?.sideMenuMyPages,
      myPagesRegisterPage: siteinfoCollection?.items[0]?.myPagesRegisterPage,
      myPagesStartPage: siteinfoCollection?.items[0]?.myPagesStartPage,
      sideMenuServicePages: siteinfoCollection?.items[0]?.sideMenuServicePages,
      minimumOrderValue: minimumOrderValue?.[marketConfig.code],
      showCheckoutMadeToOrderDisclaimer:
        siteinfoCollection?.items[0]?.showCheckoutMadeToOrderDisclaimer ?? false,
      pages,
    };
  }, [
    wishlist?.items,
    campaigns?.items,
    siteinfoCollection?.items,
    marketConfig,
    minimumOrderValue,
    pages,
  ]);

  return (
    <SiteInfoContext.Provider value={value}>
      {children}
    </SiteInfoContext.Provider>
  );
};

export const useSiteInfo = () => {
  const context = useContext(SiteInfoContext);

  if (context === undefined) {
    throw new Error("useSiteinfo must be used within a SiteInfoProvider");
  }

  return context;
};
